import { Box, Typography } from "@mui/material";

/** UTILS */
import { DecimalString, DepositRefundDeduct } from "../../../api/deposit-refund";
import { colors } from "../../../constants/colors";
import { DASH, bahtToView, deductToView, toAmount } from "../depositRefund.utils";

/** STYLE */
import * as Style from "../depositRefund.style";

type Props = {
  deducts: DepositRefundDeduct[];
  /** ยอดที่ได้รับการพิจารณา = ผลรวม remain ทุกบรรทัด (backend คำนวณมาให้แล้ว) */
  approvedAmount?: DecimalString | null;
};

/**
 * ตาราง "รายละเอียดการคำนวณเงินคืน" แบบอ่านอย่างเดียว
 * ใช้ร่วมกันระหว่าง modal รายละเอียดกับ modal ประวัติการพิจารณา เพราะดีไซน์เป็นการ์ดเดียวกัน
 *
 * คืน null เมื่อไม่มี deducts — backend ส่ง [] มาเมื่อยังไม่พิจารณา หรือไม่อนุมัติ
 * (ดีไซน์ก็ไม่มีการ์ดนี้ในเคสไม่อนุมัติ)
 */
export default function DeductTable({ deducts, approvedAmount }: Props) {
  if (!deducts?.length) return null;

  /** ยอดที่หักแสดงเป็นค่าลบสีแดงตามดีไซน์ แม้ยอดจะเป็น 0 ก็ยังขึ้น "-0" */
  const deductToViewMinus = (value?: DecimalString | null) =>
    toAmount(value) === null ? DASH : `-${deductToView(value)}`;

  return (
    <Box
      sx={{
        border: `1px solid ${colors.lightGray}`,
        borderRadius: "8px",
        backgroundColor: colors.white,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          fontSize: 16,
          fontWeight: 600,
          borderBottom: `1px solid ${colors.lightGray}`,
        }}
      >
        รายละเอียดการคำนวณเงินคืน
      </Box>

      <Style.DeductRow
        sx={{
          backgroundColor: colors.extraLightGray,
          fontSize: 14,
          color: colors.textLightGray,
          py: 1,
        }}
      >
        <span>รายการ</span>
        <span>ยอดตั้งต้น</span>
        <span>ยอดที่หัก</span>
        <span>คงเหลือ</span>
      </Style.DeductRow>

      {deducts.map((line, index) => (
        <Style.DeductRow key={line.id_deposit_refund_deduct ?? `${line.deduct_type}-${index}`}>
          {/* label เป็น snapshot ตอนพิจารณา เอาไปโชว์ตรงๆ ได้ */}
          <span>{line.label}</span>
          <span>{deductToView(line.base_amount)}</span>
          <span style={{ color: colors.red }}>{deductToViewMinus(line.deduct_amount)}</span>
          <span style={{ fontWeight: 600 }}>{deductToView(line.remain_amount)}</span>
        </Style.DeductRow>
      ))}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: 2,
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${colors.lightGray}`,
        }}
      >
        <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
          ยอดเงินที่ได้รับการพิจารณา
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          {bahtToView(approvedAmount)}
        </Typography>
      </Box>
    </Box>
  );
}
